import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import useNodeEditor from '../../Components/useNodeEditor';
import SUTextField from '../../Components/SUTextField';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import TriggerTypeField from '../../Components/TriggerTypeField';
import { NODE_THEMES } from '../../Components/NodeTheme';
import {
  InfoCallout,
  CollapsibleSection,
  ToggleField,
  boolOption,
} from '../../Components/NodeFormKit';
import Select from 'react-select';
import { sendToast } from '../../../../plugins/toastAll';
import { FaEnvelope } from 'react-icons/fa';
import { FaRegImage, FaRegFileLines } from 'react-icons/fa6';
import { BsCameraVideo } from 'react-icons/bs';
import useAPI from '../../../../../hooks/useAPI';

const TEMPLATE_NODE_COLOR = '#00A884';

const Template = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  watchSelectedNode,
  contextVariables,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm({});

  const watchMediaLink = watch('parameterMediaLink');

  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;

  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [dropdownAdvancedOptions, setDropdownAdvancedOptions] = useState(false);
  const { handleInputChange, loadData, saveNode, getIsDirty, markClean } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });
  const templatesReadyRef = useRef(false);
  const advancedCount = [
    selectedNode.data?.advanced?.closeChatBot,
    selectedNode.data?.advanced?.nextFlow,
  ].filter((option) => option?.id === true).length;
  const { urlAPI_2, fetchWithAuth } = useAPI();

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const url = urlAPI_2 + 'templates';
      const response = await fetchWithAuth(url, { method: 'GET', mode: 'cors' });
      const result = await response.json();
      if (result?.type !== 'success') {
        setTemplates([]);
        return;
      }
      const approvedTemplates = (Array.isArray(result?.data) ? result.data : [])
        .filter((tem) => tem?.status === 'APROBADA' && tem?.isActive === true)
        .map((tem) => {
          const bodyComp = Array.isArray(tem.content)
            ? tem.content.find((c) => c.type === 'BODY')
            : null;
          const bodyText = bodyComp?.text || '';
          const paramMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
          const maxParam = paramMatches.length > 0
            ? Math.max(...paramMatches.map((m) => parseInt(m.replace(/[{}]/g, ''))))
            : 0;
          const body_params = Array.from({ length: maxParam }, (_, i) => `Parámetro ${i + 1}`);
          return {
            label: `${tem.name} (#${tem.id})`,
            value: String(tem.id),
            template: { ...tem, body_params, bodyText },
          };
        });
      setTemplates(approvedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };


  const getData = () => {
    loadData({
      title: selectedNode.data?.title ?? 'Enviar Plantilla',
      user_response: selectedNode.data?.user_response ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
    });
    templatesReadyRef.current = false;
  };

  const handleTemplateChange = (selectedOption) => {
    handleInputChange('template_id', selectedOption ? selectedOption.value : null);
    handleInputChange('template_name', selectedOption ? selectedOption.label : null);
    handleInputChange('parametersBody', []);
    handleInputChange('parametersMedia', {});
    handleInputChange('parameterUrl', null);
    const bodyParams = selectedOption?.template?.body_params || selectedOption?.template?.bodyParams || [];
    bodyParams.forEach((_, idx) => setValue(`param_body_${idx}`, ''));
    setValue('parameterUrl', '');
    setValue('parameterMediaLink', '');
    setValue('parameterMediaName', '');
  };

  const saveData = (values) => {
    if (!selectedNode.data?.template_id) {
      sendToast({ type: 'error', message: 'Debes seleccionar una plantilla.' });
      return;
    }

    setLoadingUpdate(true);

    const currentTemplateOption = templates.find(
      (opt) => String(opt.value) === String(selectedNode.data?.template_id)
    ) || null;
    const bodyParams = currentTemplateOption?.template?.body_params ||
      currentTemplateOption?.template?.bodyParams || [];
    const parametersBody = bodyParams.map((_, idx) => getValues(`param_body_${idx}`) || '');

    const header = Array.isArray(currentTemplateOption?.template?.content)
      ? currentTemplateOption.template.content.find((c) => c.type === 'HEADER')
      : null;
    const headerMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header?.format)
      ? header.format.toLowerCase()
      : null;
    const mediaLink = (getValues('parameterMediaLink') || '').trim();

    let parametersMedia = {};
    if (headerMediaType && mediaLink) {
      parametersMedia = { type: headerMediaType, link: mediaLink };
      if (headerMediaType === 'document') {
        parametersMedia.nameFile = (getValues('parameterMediaName') || '').trim();
      }
    }

    const data = {
      title: values.title,
      user_response: values.user_response || null,
      trigger_type: values.trigger_type || 'message',
      template_id: selectedNode.data.template_id || null,
      template_name: selectedNode.data.template_name || null,
      parametersBody,
      parametersMedia,
      parameterUrl: getValues('parameterUrl') || null,
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
      sendToast({ type: 'success', message: 'Nodo actualizado exitosamente.' });
    });
  };

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    const currentTemplateOption = templates.find(
      (opt) => String(opt.value) === String(selectedNode.data?.template_id)
    ) || null;
    const bodyParams = currentTemplateOption?.template?.body_params ||
      currentTemplateOption?.template?.bodyParams || [];
    const parametersBody = selectedNode.data?.parametersBody || [];
    bodyParams.forEach((_, idx) => {
      setValue(`param_body_${idx}`, parametersBody[idx] || '');
    });
    setValue('parameterUrl', selectedNode.data?.parameterUrl || '');
    setValue('parameterMediaLink', selectedNode.data?.parametersMedia?.link || '');
    setValue('parameterMediaName', selectedNode.data?.parametersMedia?.nameFile || '');

    if (templates.length && !templatesReadyRef.current) {
      templatesReadyRef.current = true;
      markClean();
    }
  }, [selectedNode.data?.template_id, templates]);

  const selectedTemplateOption = templates.find(
    (opt) => String(opt.value) === String(selectedNode.data?.template_id)
  ) || null;
  const selectedTemplate = selectedTemplateOption?.template;
  const bodyParams = selectedTemplate?.body_params || selectedTemplate?.bodyParams || [];
  const urlButton = (() => {
    const btnsComp = Array.isArray(selectedTemplate?.content)
      ? selectedTemplate.content.find((c) => c.type === 'BUTTONS')
      : null;
    return btnsComp?.buttons?.find(
      (btn) => btn.type === 'URL' && /\{\{\d+\}\}/.test(btn.url || '')
    ) || null;
  })();

  const mediaHeader = (() => {
    const header = Array.isArray(selectedTemplate?.content)
      ? selectedTemplate.content.find((c) => c.type === 'HEADER')
      : null;
    if (!header || !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) return null;
    return { format: header.format, type: header.format.toLowerCase() };
  })();

  const MEDIA_LABELS = {
    image: { title: 'Imagen del encabezado', hint: 'JPG o PNG', Icon: FaRegImage },
    video: { title: 'Video del encabezado', hint: 'MP4', Icon: BsCameraVideo },
    document: { title: 'Documento del encabezado', hint: 'PDF', Icon: FaRegFileLines },
  };

  const renderBodyPreview = (text) => {
    const parts = text.split(/(\{\{\d+\}\})/g);
    return parts.map((part, i) =>
      /\{\{\d+\}\}/.test(part) ? (
        <span
          key={i}
          style={{
            backgroundColor: 'rgba(0,168,132,0.25)',
            color: '#00A884',
            borderRadius: '3px',
            padding: '1px 4px',
            fontWeight: 600,
          }}
        >
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  return (
    <NodeEditorChrome
      icon={FaEnvelope}
      color={TEMPLATE_NODE_COLOR}
      typeName="Plantilla"
      title={truncateText(selectedNode.data.title)}
      onRemove={() => removeNode(selectedNode.id)}
      canRemove={!selectedNode.data.first}
      onMouseDown={onMouseDown}
      isDragging={isDragging}
      onClose={() => {
        reset();
        handleSelectedNode(null);
        setDropdownAdvancedOptions(false);
      }}
      formId="templateForm"
      saving={loadingUpdate}
      getIsDirty={getIsDirty}
      saveDisabled={loadingUpdate}
      isDark={isDark}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      toastAutoClose={5000}
    >
        <form id="templateForm" onSubmit={handleSubmit(saveData)} style={{ width: '100%' }}>
          <div className="col p-0">
            <SUTextField
              id="title"
              name="title"
              label="Título:"
              placeholder="Enviar Plantilla"
              type="text"
              selectedNode={selectedNode}
              getValues={getValues}
              setValue={setValue}
              errors={errors}
              {...register('title', {
                required: { value: true, message: 'Campo requerido.' },
                pattern: { value: /\S+/, message: 'No puede ser solo espacios en blanco' },
              })}
            />
          </div>

          {!selectedNode.data.first && (
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>
          )}

          {!selectedNode.data.first && (
            <div className="col p-0 mt-4">
              <SUTextField
                id="user_response"
                name="user_response"
                label="Respuesta del cliente:"
                placeholder="Ej: click / confirm"
                hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ."
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('user_response', {
                  required: {
                    value: !selectedNode.data.first,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
              <InfoCallout title="Importante:">
                Los nodos se pueden activar con múltiples respuestas según estén
                configuradas. Para ello debe tener el siguiente formato:
                Respuesta 1 / Respuesta 2 / Respuesta 3.
              </InfoCallout>
            </div>
          )}

          <div className="col p-0 mt-4">
            <label style={{ color: theme.textSecondary, fontWeight: 600 }}>Plantilla:</label>
            <div className="su-control-group">
              <Select
                options={templates}
                value={selectedTemplateOption}
                onChange={handleTemplateChange}
                placeholder={loadingTemplates ? 'Cargando plantillas...' : 'Selecciona una plantilla'}
                isClearable
                isLoading={loadingTemplates}
                styles={isDark == 'true' ? customStylesDark : customStyles}
                className="form-control basic-multi-select"
                classNamePrefix="select"
                noOptionsMessage={() => 'Sin opciones'}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />
            </div>
          </div>

          {selectedTemplate && (
            <div className="mt-3">
              <label style={{ color: theme.textSecondary, fontWeight: 600, fontSize: 12, marginBottom: '6px', display: 'block' }}>
                Vista previa:
              </label>
              <div
                style={{
                  backgroundColor: isDark == 'true' ? '#0b141a' : '#e5ddd5',
                  borderRadius: '8px',
                  padding: '12px 10px',
                }}
              >
                <div
                  style={{
                    backgroundColor: isDark == 'true' ? '#202c33' : '#fff',
                    borderRadius: '0 8px 8px 8px',
                    padding: '10px 12px 6px',
                    maxWidth: '92%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                    position: 'relative',
                  }}
                >
                  {/* Header */}
                  {(() => {
                    const h = Array.isArray(selectedTemplate.content)
                      ? selectedTemplate.content.find((c) => c.type === 'HEADER')
                      : null;
                    if (!h) return null;
                    if (h.format === 'TEXT' && h.text) {
                      return (
                        <p style={{ fontWeight: 600, fontSize: 13, color: isDark == 'true' ? '#e9edef' : '#111b21', marginBottom: '6px', lineHeight: 1.4 }}>
                          {h.text}
                        </p>
                      );
                    }
                    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(h.format)) {
                      const icons = { IMAGE: '🖼️', VIDEO: '🎬', DOCUMENT: '📄' };
                      const link = (watchMediaLink || '').trim();
                      const isStaticImage =
                        h.format === 'IMAGE' && /^https?:\/\//i.test(link) && !link.includes('{{');

                      if (isStaticImage) {
                        return (
                          <img
                            src={link}
                            alt="Encabezado"
                            style={{
                              width: '100%',
                              maxHeight: '140px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        );
                      }

                      return (
                        <div
                          style={{
                            backgroundColor: isDark == 'true' ? '#2a3942' : '#f0f0f0',
                            borderRadius: '6px',
                            height: '72px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '8px',
                            gap: '2px',
                            padding: '0 8px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: 22 }}>{icons[h.format]}</span>
                            <span style={{ fontSize: 11, color: '#8696a0', fontWeight: 600 }}>{h.format}</span>
                          </div>
                          {link ? (
                            <span
                              style={{
                                fontSize: 10,
                                color: '#00A884',
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {link}
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: '#d9534f', fontWeight: 600 }}>
                              Sin URL configurada
                            </span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Body */}
                  {selectedTemplate.bodyText && (
                    <p
                      style={{
                        fontSize: 13,
                        color: isDark == 'true' ? '#e9edef' : '#111b21',
                        marginBottom: '4px',
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {renderBodyPreview(selectedTemplate.bodyText)}
                    </p>
                  )}

                  {/* Footer */}
                  {(() => {
                    const f = Array.isArray(selectedTemplate.content)
                      ? selectedTemplate.content.find((c) => c.type === 'FOOTER')
                      : null;
                    if (!f?.text) return null;
                    return (
                      <p style={{ fontSize: 11, color: '#8696a0', marginBottom: '4px', marginTop: '2px' }}>
                        {f.text}
                      </p>
                    );
                  })()}

                  {/* Timestamp */}
                  <div style={{ textAlign: 'right', marginTop: '2px' }}>
                    <span style={{ fontSize: 10, color: '#8696a0' }}>ahora ✓✓</span>
                  </div>
                </div>

                {/* Buttons */}
                {(() => {
                  const btnsComp = Array.isArray(selectedTemplate.content)
                    ? selectedTemplate.content.find((c) => c.type === 'BUTTONS')
                    : null;
                  if (!btnsComp?.buttons?.length) return null;
                  return (
                    <div style={{ marginTop: '3px' }}>
                      {btnsComp.buttons.map((btn, i) => (
                        <div
                          key={i}
                          style={{
                            backgroundColor: isDark == 'true' ? '#202c33' : '#fff',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginTop: '2px',
                            textAlign: 'center',
                            color: '#00A884',
                            fontWeight: 600,
                            fontSize: 13,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}
                        >
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {selectedTemplate && mediaHeader && (
            <div className="col p-0 mt-4">
              <label
                style={{
                  color: theme.textSecondary,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {React.createElement(MEDIA_LABELS[mediaHeader.type].Icon, {
                  style: { fontSize: '15px', color: TEMPLATE_NODE_COLOR },
                })}
                {MEDIA_LABELS[mediaHeader.type].title}:
              </label>
              <div className="mt-2">
                <SUTextField
                  id="parameterMediaLink"
                  name="parameterMediaLink"
                  label="URL del archivo:"
                  placeholder="https://... o {{variable}}"
                  type="text"
                  showVariables
                  contextVariables={contextVariables}
                  selectedNode={selectedNode}
                  getValues={getValues}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
              {mediaHeader.type === 'document' && (
                <div className="mt-2">
                  <SUTextField
                    id="parameterMediaName"
                    name="parameterMediaName"
                    label="Nombre del archivo:"
                    placeholder="documento.pdf"
                    type="text"
                    selectedNode={selectedNode}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                  />
                </div>
              )}
              <InfoCallout>
                Esta plantilla tiene un encabezado de tipo{' '}
                <strong>{mediaHeader.format}</strong>. Ingresa una URL pública (
                {MEDIA_LABELS[mediaHeader.type].hint}) o una variable dinámica,
                por ejemplo la foto del conductor devuelta por un nodo de API.
              </InfoCallout>
            </div>
          )}

          {selectedTemplate && bodyParams.length > 0 && (
            <div className="col p-0 mt-4">
              <label style={{ color: theme.textSecondary, fontWeight: 600 }}>Parámetros del cuerpo:</label>
              {bodyParams.map((param, idx) => (
                <div key={idx} className="mt-2">
                  <SUTextField
                    id={`param_body_${idx}`}
                    name={`param_body_${idx}`}
                    label={`{{${idx + 1}}}:`}
                    placeholder="Valor estático o {{variable}}"
                    type="text"
                    showVariables
                    contextVariables={contextVariables}
                    selectedNode={selectedNode}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                  />
                </div>
              ))}
              <InfoCallout>
                Puedes ingresar un valor estático o seleccionar una variable
                dinámica con el icono de variables.
              </InfoCallout>
            </div>
          )}


          {selectedTemplate && urlButton && (
            <div className="col p-0 mt-4">
              <label style={{ color: theme.textSecondary, fontWeight: 600 }}>
                Parámetro dinámico del botón (URL):
              </label>
              <div className="mt-2">
                <SUTextField
                  id="parameterUrl"
                  name="parameterUrl"
                  label={`${urlButton.url}`}
                  placeholder="Valor estático o {{variable}}"
                  type="text"
                  showVariables
                  contextVariables={contextVariables}
                  selectedNode={selectedNode}
                  getValues={getValues}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
              <InfoCallout>
                Este valor reemplaza el parámetro dinámico ({'{{1}}'}) de la URL del botón
                &quot;{urlButton.text}&quot;.
              </InfoCallout>
            </div>
          )}

          <div className="col p-0 mt-4">
            <CollapsibleSection
              title="Opciones avanzadas"
              count={advancedCount}
              open={dropdownAdvancedOptions}
              onToggle={() => setDropdownAdvancedOptions(!dropdownAdvancedOptions)}
            >

                    {!selectedNode.data?.first && (
                      <div className="col p-2">
                        <ToggleField
                          id="closeChatBot"
                          label="Cerrar la conversación"
                          hint="Marca este nodo como el último punto de la conversación."
                          checked={
                            selectedNode.data?.advanced?.closeChatBot?.id ===
                            true
                          }
                          onChange={(checked) =>
                            handleInputChange(
                              'advanced.closeChatBot',
                              boolOption(checked)
                            )
                          }
                        />
                      </div>
                    )}

                    {!selectedNode.data?.first && (
                    <div className="col p-2">
                      <ToggleField
                        id="nextFlow"
                        label="Saltar nodo"
                        hint="El siguiente nodo se ejecuta automáticamente."
                        last
                        checked={
                          selectedNode.data?.advanced?.nextFlow?.id === true
                        }
                        onChange={(checked) => {
                          handleInputChange(
                            'advanced.nextFlow',
                            boolOption(checked)
                          );
                          if (!checked) {
                            handleInputChange(
                              'advanced.nextFlowDelaySeconds',
                              ''
                            );
                          }
                        }}
                      />

                      {selectedNode.data?.advanced?.nextFlow?.id === true && (
                        <div className="mt-2">
                          <label style={{ color: theme.textSecondary, fontWeight: 600 }}>
                            Tiempo de espera (segundos):
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            max="60"
                            step="1"
                            value={selectedNode.data?.advanced?.nextFlowDelaySeconds ?? ''}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              if (rawValue === '') {
                                handleInputChange('advanced.nextFlowDelaySeconds', '');
                                return;
                              }
                              const parsedValue = Number(rawValue);
                              const boundedValue = Number.isFinite(parsedValue)
                                ? Math.min(Math.max(parsedValue, 0), 60)
                                : 0;
                              handleInputChange('advanced.nextFlowDelaySeconds', String(Math.floor(boundedValue)));
                            }}
                            placeholder="0"
                          />
                          <p style={{ fontSize: 11, color: theme.textSecondary, marginTop: '6px' }}>
                            Si dejas 0 o vacío, el salto será inmediato. Máximo permitido: 60 segundos.
                          </p>
                        </div>
                      )}
                    </div>
                    )}
            </CollapsibleSection>
          </div>
        </form>
    </NodeEditorChrome>
  );
};

export default Template;
